import {
  DownloadPageEventType,
  IEmbedConfig,
  ProjectCreationMethod,
  Replace,
  SocialPostCaptions,
  SoundWaveGeneration,
  TemplateType,
} from 'types';
import {
  ClipSuggestionStatus,
  FeedbackType,
  SocialPostCaptionInfoStatus,
} from '../podcast-service';
import { IApiAction, IApiResponse, NormalizationResult } from '../types';

export const ACTION_KEY = 'CREATION_SERVICE';

export enum ServiceMethod {
  CREATE = 'CREATE',
  CREATION_GET = 'CREATION_GET',
  GET_CREATION_CLIP_SUGGESTIONS = 'GET_CREATION_CLIP_SUGGESTIONS',
  ENABLE_CREATION_CLIP_SUGGESTION = 'ENABLE_CREATION_CLIP_SUGGESTION',
  RECEIVE_CREATION_SUGGESTION_EVENT = 'RECEIVE_CREATION_SUGGESTION_EVENT',
  CREATE_CREATION_CLIP_SUGGESTION_FEEDBACK = 'CREATE_CREATION_CLIP_SUGGESTION_FEEDBACK',
  CREATE_CREATION_CLIP_SUGGESTION = 'CREATE_CREATION_CLIP_SUGGESTION',
  GET_CREATION_CLIP_SUGGESTION = 'GET_CREATION_CLIP_SUGGESTION',
  GET_CREATION_CLIP_SUGGESTIONS_PROGRESS = 'GET_CREATION_CLIP_SUGGESTIONS_PROGRESS',
  REPROCESS_CREATION_SUGGESTION = 'REPROCESS_CREATION_SUGGESTIONS',
  CREATE_SOCIAL_POST_CAPTIONS = 'CREATE_SOCIAL_POST_CAPTIONS',
  GET_SOCIAL_POST_CAPTIONS = 'GET_SOCIAL_POST_CAPTIONS',
  GET_SOCIAL_POST_CAPTIONS_JOB = 'GET_SOCIAL_POST_CAPTIONS_JOB',
  REGENERATE_PROJECT = 'REGENERATE_PROJECT',
  GET_PROJECT_LATEST_REGENERATION = 'GET_PROJECT_LATEST_REGENERATION',
  GET_PROJECT_REGENERATION = 'GET_PROJECT_REGENERATION',
  GENERATE_PROJECT_NAME = 'GENERATE_PROJECT_NAME',
  GET_PROJECT_NAME_GENERATION = 'GET_PROJECT_NAME_GENERATION',
  CREATE_CREATION_ASSET = 'CREATE_CREATION_ASSET',
  CREATE_VIDEO_ZIP_REQUEST = 'CREATE_VIDEO_ZIP_REQUEST',
  GET_VIDEO_ZIP_REQUEST = 'GET_VIDEO_ZIP_REQUEST',
}

export enum CreationStatus {
  SUBMITTED = 'submitted',
  PROCESSING_ASSETS = 'processingAssets',
  CREATING_CONFIG = 'creatingConfig',
  EXPORTING_VIDEO = 'exportingVideo',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export enum AssetStatus {
  COMPLETED = 'completed',
  FAILED = 'failed',
  NOT_FOUND = 'notFound',
  PROCESSING = 'processing',
}

export interface CreationRequestVideoInfo {
  alreadyProcessed: boolean;
  currentlyProcessing: boolean;
  videoId: number;
}

export interface CreationRequest {
  audioFile?: Blob;
  audioPodcastId?: string;
  audioRemoteEpisodeId?: string;
  audioUrl?: string;
  videoFile?: Blob;
  videoUrl?: string;
  isVideoTranscriptEnabled?: boolean;
  videoLanguage?: string;
  videoTrimStartMillis?: number;
  videoTrimEndMillis?: number;
  entireAudioInstanceId?: number;
  entireAudioTrimStartMillis?: number;
  entireAudioTrimEndMillis?: number;
  preferredAudioWavetype?: SoundWaveGeneration;
  isAudioTranscriptEnabled?: boolean;
  templateId?: string;
  templateType?: TemplateType;
  projectCreateMethod?: ProjectCreationMethod;
  projectName?: string;
  baseConfigJson?: string;
  entireAudioTranscriptUrl?: string;
  clippedAudioTranscriptUrl?: string;
  initiateExportVideo?: boolean;
  videoFrameHeight?: number;
  videoFrameWidth?: number;
  videoFrameQuality?: number;
  videoFrameRate?: number;
  aspectRatioWidth?: number;
  aspectRatioHeight?: number;
  customTraceId?: string;
  traceId?: string;
  isEddy?: boolean;
  audioLanguage?: string;
  uploadedVideoId?: number;
  videoInfo?: CreationRequestVideoInfo;
  userId?: number;
  isVideoConfigLocked?: boolean;
  useYoutubeMostReplayed?: boolean;
  uploadedRecordingId?: number;
  captionSourceEddyProjectCuid?: string;
}

export type CreationActionOptions = Replace<
  Omit<CreationRequest, 'audioUrl' | 'audioFile' | 'videoUrl' | 'videoFile'>,
  {
    audioSource?: string | File;
    videoSource?: string | File;
    baseConfigJson?: IEmbedConfig;
    projectUuid?: string;
  }
>;

export type CreationVideoExportOptions = Pick<
  CreationActionOptions,
  | 'videoFrameWidth'
  | 'videoFrameQuality'
  | 'videoFrameHeight'
  | 'videoFrameRate'
>;

export type BaseCreationAssetInfo = {
  audioLanguage?: string;
  preferredAudioWavetype?: SoundWaveGeneration;
  isAudioTranscriptEnabled?: boolean;
  projectName?: string;
  projectCreateMethod?: ProjectCreationMethod;
  isEddy?: boolean;
};

export type CreationAudioInfo = BaseCreationAssetInfo & {
  audioDirectUploadBucket?: string;
  audioDirectUploadKey?: string;
  audioUrl?: string;
  recordingId?: number;
  audioPodcastId?: string;
  audioRemoteEpisodeId?: string;
};

export type CreationVideoInfo = BaseCreationAssetInfo & {
  uploadedVideoId?: number;
  isVideoTranscriptEnabled?: boolean;
};

export type CreateCreationAssetArgs = CreationAudioInfo | CreationVideoInfo;

export interface AssetStatuses {
  audioStatus: AssetStatus;
  waveformStatus: AssetStatus;
}

export interface CreationResponse {
  assetStatuses: AssetStatus;
  createdAt: number;
  id: number;
  initiateVideoExport: boolean;
  requestObj: CreationRequest;
  status: CreationStatus;
}

export type CreationArgs = [CreationRequest];
export type GetCreationArgs = [number];

export type CreateCreationAssetResponse = {
  uploadedRecordingId: number;
  captionSourceEddyProjectCuid: string;
};

export interface CreationResult {
  entities: {
    creations: {
      [key: number]: CreationResponse;
    };
  };
  result: number;
}

type ApiAction<M extends ServiceMethod, A> = IApiAction<
  typeof ACTION_KEY,
  M,
  A
>;

export type CreationAction = ApiAction<ServiceMethod.CREATE, CreationArgs>;
export type GetCreationAction = ApiAction<
  ServiceMethod.CREATION_GET,
  GetCreationArgs
>;

export type GetCreationClipSuggestionsArgs = [
  number, // creationRequestId
];

export type GetCreationClipSuggestionArgs = [
  number, // creationRequestId
  number, // suggestionId
];

export type GetCreationClipSuggestionsProgressArgs = [
  number, // creationRequestId
];

export type CreateCreationClipSuggestionArgs = [
  number, // creationRequestId
  number, // replaceSuggestionId
  number, // newTrimStartMillis
  number, // newTrimEndMillis
];

export type EnableCreationClipSuggestionArgs = [
  number, // creationRequestId
  number, // suggestionId
];

export type EnableCreationClipSuggestionResult = null;

export type CreationSuggestionEventType = DownloadPageEventType;

export type ReceiveCreationSuggestionEventArgs = {
  creationRequestId: number;
  suggestionId: number;
  eventType: CreationSuggestionEventType;
};

export type ReceiveCreationSuggestionEventResult = null;

export type RegenerateProjectArgs = {
  projectId: string;
  baseConfigJson: IEmbedConfig;
  captionSourceEddyProjectCuid?: string;
  videoFrameHeight?: number;
  videoFrameWidth?: number;
  videoFrameQuality?: number;
  videoFrameRate?: number;
};

export type ProjectRegeneration = {
  id: number;
  requestObj: IEmbedConfig;
  status: CreationStatus;
  widgetId: string;
};

export type RegenerateProjectAction = ApiAction<
  ServiceMethod.REGENERATE_PROJECT,
  RegenerateProjectArgs
>;

export type GenerateProjectNameFormality = 'neutral' | 'casual' | 'formal';

export type GenerateProjectNameTone = 'direct' | 'empathetic' | 'witty';

export type GenerateProjectNameArgs = {
  projectUuid: string;
  formality: GenerateProjectNameFormality;
  tone: GenerateProjectNameTone;
};

export type GenerateProjectNameAction = ApiAction<
  ServiceMethod.GENERATE_PROJECT_NAME,
  GenerateProjectNameArgs
>;

export type GenerateProjectNameStatus =
  | 'queued'
  | 'processing'
  | 'completed'
  | 'failed';

export type ProjectNameGeneration = {
  id: number;
  generatedName: string | null;
  status: GenerateProjectNameStatus;
};

export type GetProjectNameGenerationArgs = [
  number, // projectNameGenerationId
];

export type GetProjectNameGenerationAction = ApiAction<
  ServiceMethod.GET_PROJECT_NAME_GENERATION,
  GetProjectNameGenerationArgs
>;

export type ReprocessCreationSuggestionArgs = {
  creationRequestId: number;
  suggestionId: number;
  newTrimStartMillis?: number;
  newTrimEndMillis?: number;
  configJson?: IEmbedConfig;
  /**
   * flag for if we should only create a new preview for the suggestion. otherwise
   * it will create and export a new config
   */
  isPreviewRequest?: boolean;
};

export type CreateCreationClipSuggestionFeedbackArgs = [
  FeedbackType, // feedbackType:
  number, // creationRequestId
  number, // suggestionId
];

export type CreationSuggestionTranscriptSegment = {
  speaker?: string;
  text: string;
};

export type CreationClipSuggestions = {
  eddyProjectCuid: string;
  suggestions: CreationClipSuggestion[];
};

export interface CreationClipSuggestion {
  suggestionId: number;
  startMillis: number;
  endMillis: number;
  /**
   * if preview was created before the latest transcript updates
   */
  isPreviewOutdated?: boolean;
  /**
   * if a preview was requested for this clip suggestion
   */
  isPreviewRequested?: boolean;
  isUserSelected?: boolean;
  /**
   * if video was created before the latest transcript updates
   */
  isVideoOutdated?: boolean;
  /**
   * widget that is preview only and has not been exported
   */
  previewWidgetId?: string;
  text?: string;
  transcriptSegments?: CreationSuggestionTranscriptSegment[];
  /**
   * widget associated with the exported video
   */
  widgetId?: string;
  videoUrl?: string;
  videoThumbnailUrl?: string;
  status?: ClipSuggestionStatus;
  /**
   * the generated or user-edited name of the project associated with a clip.
   * This value may come from the backend (e.g., generated using LLM with
   * transcript data) or be manually edited by the user.
   */
  projectName?: string;
  /**
   * indicates whether the projectName has been edited and should be used as the
   * preferred title
   */
  isProjectNameEdited?: boolean;
}

export interface NormalizeGroupClipSuggestions {
  entities: {
    creationClipSuggestions: {
      clipGroup: CreationClipSuggestions;
    };
    creationClipSuggestion: Record<string, CreationClipSuggestion>;
  };
  result: 'clipGroup';
}

export type GetCreationClipSuggestionsResult = NormalizeGroupClipSuggestions;

export type GetCreationClipSuggestionsAction = ApiAction<
  ServiceMethod.GET_CREATION_CLIP_SUGGESTIONS,
  GetCreationClipSuggestionsArgs
>;

export type GetCreationClipSuggestionAction = ApiAction<
  ServiceMethod.GET_CREATION_CLIP_SUGGESTION,
  GetCreationClipSuggestionArgs
>;

export type GetCreationClipSuggestionsProgressAction = ApiAction<
  ServiceMethod.GET_CREATION_CLIP_SUGGESTIONS_PROGRESS,
  GetCreationClipSuggestionsProgressArgs
>;

export type NormalizedCreationCilpSuggestion = {
  entities: {
    creationClipSuggestion: Record<string, CreationClipSuggestion>;
  };
  result: string;
};

export type GetCreationClipSuggestionResult = NormalizedCreationCilpSuggestion;

export type CreationSuggestionsProgress = {
  progress: number;
  status: CreationStatus;
  estimatedCompletedAt: number;
};

export type NormalizeCreationSuggestionsProgress = {
  entities: {
    creationSuggestionsProgress: CreationSuggestionsProgress;
  };
  result: CreationStatus;
};

export type GetCreationClipSuggestionsProgressResult = NormalizeCreationSuggestionsProgress;

export type CreateCreationClipSuggestionAction = ApiAction<
  ServiceMethod.CREATE_CREATION_CLIP_SUGGESTION,
  CreateCreationClipSuggestionArgs
>;

export type CreateCreationClipSuggestionResult = CreationClipSuggestion;

export type ReprocessCreationSuggestionAction = ApiAction<
  ServiceMethod.REPROCESS_CREATION_SUGGESTION,
  ReprocessCreationSuggestionArgs
>;

export type CreateSocialPostCaptionsAction = ApiAction<
  ServiceMethod.CREATE_SOCIAL_POST_CAPTIONS,
  CreateSocialPostCaptionsArgs
>;

export type GetSocialPostCaptionsAction = ApiAction<
  ServiceMethod.GET_SOCIAL_POST_CAPTIONS,
  GetSocialPostCaptionsArgs
>;

export type GetSocialPostCaptionsJobAction = ApiAction<
  ServiceMethod.GET_SOCIAL_POST_CAPTIONS_JOB,
  GetSocialPostCaptionsJobArgs
>;

export type ReprocessCreationSuggestionResult = void;

export type CreateSocialPostCaptionsArgs = [
  number, // creationRequestId
];
export type CreateSocialPostCaptionsResult = {
  jobId: number;
};

export type GetSocialPostCaptionsArgs = [
  number, // creationRequestId
];

export type GetSocialPostCaptionsJobArgs = [
  number, // creationRequestId
  number,
];

export type GetProjectLatestRegenerationArgs = [
  string, // projectId
];

export type GetProjectLatestRegenerationAction = ApiAction<
  ServiceMethod.GET_PROJECT_LATEST_REGENERATION,
  GetProjectLatestRegenerationArgs
>;

export type GetProjectRegenerationArgs = [
  string, // projectId
  number, // projectRegenerationId
];

export type GetProjectRegenerationAction = ApiAction<
  ServiceMethod.GET_PROJECT_REGENERATION,
  GetProjectRegenerationArgs
>;

export type CreateCreationAssetAction = ApiAction<
  ServiceMethod.CREATE_CREATION_ASSET,
  CreateCreationAssetArgs
>;

export type VideoZipRequestStatus =
  | 'notFound'
  | 'processing'
  | 'completed'
  | 'failed';

export type VideoZipRequestArgs = {
  creationRequestId?: number;
  groupRequestId?: number;
  zipRequestId?: number;
};

export type VideoZip = {
  id: number;
  creationRequestId: number;
  groupRequestId: number;
  status?: VideoZipRequestStatus;
  snapshotMillis?: number;
  zipFileDownloadUrl?: string;
};

export type CreateVideoZipRequestAction = ApiAction<
  ServiceMethod.CREATE_VIDEO_ZIP_REQUEST,
  VideoZipRequestArgs
>;

export type GetVideoZipRequestAction = ApiAction<
  ServiceMethod.GET_VIDEO_ZIP_REQUEST,
  VideoZipRequestArgs
>;

export type NormalizeVideoZipRequestResult = NormalizationResult<
  'videoZipRequestsById',
  number,
  VideoZip
>;

export type NormalizeSocialPostCaptions = {
  entities: {
    videoSocialPostCaptions: SocialPostCaptionResponse;
  };
  result: number;
};

export type SocialPostCaptionResponse = {
  jobId: number;
  canGenerateSocialPostCaptions: boolean;
  status: SocialPostCaptionInfoStatus;
  socialPostCaptions: SocialPostCaptions[] | null;
};

export type GetSocialPostCaptionsResult = NormalizeSocialPostCaptions;

export interface CreationServiceDispatch {
  (action: CreationAction): Promise<IApiResponse<CreationResult>>;
  (action: GetCreationAction): Promise<IApiResponse<CreationResult>>;
  (action: GetCreationClipSuggestionsAction): Promise<
    IApiResponse<GetCreationClipSuggestionsResult>
  >;
  (action: CreateCreationClipSuggestionAction): Promise<
    IApiResponse<CreateCreationClipSuggestionResult>
  >;
  (action: GetCreationClipSuggestionAction): Promise<
    IApiResponse<GetCreationClipSuggestionResult>
  >;
  (action: GetCreationClipSuggestionsProgressAction): Promise<
    IApiResponse<GetCreationClipSuggestionsProgressResult>
  >;
  (action: ReprocessCreationSuggestionAction): Promise<
    IApiResponse<ReprocessCreationSuggestionResult>
  >;
  (action: CreateSocialPostCaptionsAction): Promise<
    IApiResponse<CreateSocialPostCaptionsResult>
  >;
  (action: GetSocialPostCaptionsAction): Promise<
    IApiResponse<GetSocialPostCaptionsResult>
  >;
  (action: GetSocialPostCaptionsJobAction): Promise<
    IApiResponse<GetSocialPostCaptionsResult>
  >;
  (action: GetProjectLatestRegenerationAction): Promise<
    IApiResponse<ProjectRegeneration>
  >;
  (action: RegenerateProjectAction): Promise<IApiResponse<ProjectRegeneration>>;
  (action: GenerateProjectNameAction): Promise<
    IApiResponse<ProjectNameGeneration>
  >;
  (action: GetProjectNameGenerationAction): Promise<
    IApiResponse<ProjectNameGeneration>
  >;
  (action: CreateCreationAssetAction): Promise<
    IApiResponse<CreateCreationAssetResponse>
  >;
  (action: CreateVideoZipRequestAction): Promise<
    IApiResponse<NormalizeVideoZipRequestResult>
  >;
  (action: GetVideoZipRequestAction): Promise<
    IApiResponse<NormalizeVideoZipRequestResult>
  >;
}
